
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Copy, Search, Share2, Brain } from "lucide-react";
import { toast } from "sonner";
import { AIInsight } from "@/types/seo";

interface KeywordInfoProps {
  keyword: string;
  volume: number | null;
  cpc?: number;
  url: string;
  aiInsights?: AIInsight[];
}

export const KeywordInfo = ({ keyword, volume, cpc, url, aiInsights }: KeywordInfoProps) => {
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copiado com sucesso!");
    } catch (err) {
      toast.error("Erro ao copiar o link");
    }
  };

  const scrollToInsights = () => {
    const insightsSection = document.querySelector('[data-section="ai-insights"]');
    if (insightsSection) {
      insightsSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const getDisplayUrl = (url: string) => {
    const urlObject = new URL(url);
    return urlObject.origin + urlObject.pathname;
  };

  const hasAIInsights = aiInsights && aiInsights.length > 0;

  return (
    <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg shadow-sm border flex-1 mx-2 sm:mx-0">
      <div className="space-y-2 sm:space-y-4">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 text-left">{keyword}</h1>

        <div className="space-y-1 sm:space-y-2">
          <div className="flex flex-col sm:flex-row gap-1 text-xs sm:text-sm text-gray-500">
            <span className="font-bold text-black">Volume de Buscas:</span>
            <span>{(volume || 0).toLocaleString('pt-BR')} pesquisas/mês</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-1 text-xs sm:text-sm text-gray-500">
            <span className="font-bold text-black">URL Analisada:</span>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              {getDisplayUrl(url)} <ArrowUpRight className="h-3 w-3" />
            </a>
          </div>

          {cpc !== undefined && (
            <div className="flex flex-col sm:flex-row gap-1 text-xs sm:text-sm text-gray-500">
              <span className="font-bold text-black">CPC médio:</span>
              <span>R$ {cpc.toFixed(2).replace('.', ',')}</span>
            </div>
          )}
        </div>

        <div className="h-[20px] sm:h-[28px]"></div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button 
            className="bg-[#4361ee] hover:bg-[#4361ee]/90 text-white h-auto py-2 text-xs sm:text-sm w-fit px-4"
            onClick={handleCopyLink}
          >
            <Share2 className="h-3 w-3 sm:h-4 sm:w-4" />
            Compartilhar Relatório
          </Button>

          <Button 
            className="bg-success hover:bg-success/90 text-white h-auto py-2 text-xs sm:text-sm w-fit px-4"
            asChild
          >
            <a 
              href="https://www.conversion.com.br/contato/?utm_campaign=SEO%20Score&utm_source=tool&utm_medium=pagina&utm_term=conv&utm_content=botao-pagina-seo-score" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Search className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
              Quer crescer seu tráfego? Fale conosco
            </a>
          </Button>

          {hasAIInsights && (
            <Button 
              className="relative overflow-hidden bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white h-auto py-2 text-xs sm:text-sm w-fit px-4 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 before:absolute before:inset-0 before:bg-gradient-to-r before:from-blue-600 before:via-purple-600 before:to-indigo-600 before:animate-pulse before:opacity-30"
              onClick={scrollToInsights}
            >
              <Brain className="h-3 w-3 sm:h-4 sm:w-4 mr-2 relative z-10" />
              <span className="relative z-10">Insights com IA</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
